/**
 * Entity Factory - Creates services from EntityDefinition.
 * HTTP routing lives in src/http/entity-factory.ts (Hono).
 */

import type {
  EntityDefinition,
  ModuleContext,
  BaseEntityService,
  CollectionEntityDefinition,
  SingleEntityDefinition,
  ViewEntityDefinition,
  ComputedEntityDefinition,
  ExternalEntityDefinition,
  TreeEntityDefinition,
  DagEntityDefinition,
  CreateEntityServiceOptions,
  EntityServiceHooks
} from '@gzl10/nexus-sdk'
import { resolveLocalized } from '@gzl10/nexus-sdk'
import type { EntityService } from './types.js'

import { composeHooks } from '@kernel/runtime/helpers/compose-hooks.js'
import { CollectionService } from '@kernel/runtime/services/collection.service.js'
import { SingleService } from '@kernel/runtime/services/single.service.js'
import { ViewService } from '@kernel/runtime/services/view.service.js'
import { ComputedService } from '@kernel/runtime/services/computed.service.js'
import { ExternalService } from '@kernel/runtime/services/external.service.js'

// Lazy-loaded services cache for tree/dag (avoid circular dependency)
let _TreeService: typeof import('@kernel/runtime/services/tree.service.js').TreeService | null = null
let _DagService: typeof import('@kernel/runtime/services/dag.service.js').DagService | null = null

async function getTreeService() {
  if (!_TreeService) {
    const mod = await import('@kernel/runtime/services/tree.service.js')
    _TreeService = mod.TreeService
  }
  return _TreeService
}

async function getDagService() {
  if (!_DagService) {
    const mod = await import('@kernel/runtime/services/dag.service.js')
    _DagService = mod.DagService
  }
  return _DagService
}

/**
 * Create service based on entity type (sync - throws for tree/dag)
 */
export function createEntityService<T extends Record<string, unknown> = Record<string, unknown>>(
  ctx: ModuleContext,
  definition: EntityDefinition,
  options?: CreateEntityServiceOptions<T>
): BaseEntityService<T> {
  const definitionHooks = 'hooks' in definition && typeof definition.hooks === 'function'
    ? definition.hooks(ctx) as EntityServiceHooks<T>
    : undefined
  const hooks = composeHooks(definitionHooks, options?.hooks)

  switch (definition.type) {
    case 'collection':
    case undefined:
      if ((definition as CollectionEntityDefinition).adapter) {
        return new ExternalService<T>(ctx, definition as unknown as ExternalEntityDefinition, hooks) as unknown as BaseEntityService<T>
      }
      return new CollectionService<T>(ctx, definition as CollectionEntityDefinition, hooks) as unknown as BaseEntityService<T>
    case 'temp':
    case 'event':
    case 'reference':
      return new CollectionService<T>(ctx, definition as CollectionEntityDefinition, hooks) as unknown as BaseEntityService<T>
    case 'single':
    case 'config':
      return new SingleService<T>(ctx, definition as SingleEntityDefinition, hooks) as unknown as BaseEntityService<T>
    case 'view':
      return new ViewService<T>(ctx, definition as ViewEntityDefinition, hooks) as unknown as BaseEntityService<T>
    case 'computed':
    case 'virtual':
      return new ComputedService<T>(ctx, definition as unknown as ComputedEntityDefinition, hooks) as unknown as BaseEntityService<T>
    case 'external':
      return new ExternalService<T>(ctx, definition as ExternalEntityDefinition, hooks) as unknown as BaseEntityService<T>
    default:
      throw new Error(`Unknown entity type: ${(definition as EntityDefinition).type}`)
  }
}

/**
 * Create service based on entity type (async - supports all types including tree/dag)
 */
export async function createEntityServiceAsync<T extends Record<string, unknown> = Record<string, unknown>>(
  ctx: ModuleContext,
  definition: EntityDefinition,
  options?: CreateEntityServiceOptions<T>
): Promise<BaseEntityService<T>> {
  const definitionHooks = 'hooks' in definition && typeof definition.hooks === 'function'
    ? definition.hooks(ctx) as EntityServiceHooks<T>
    : undefined
  const hooks = composeHooks(definitionHooks, options?.hooks)

  if (definition.type === 'tree') {
    const TreeService = await getTreeService()
    return new TreeService<T>(ctx, definition as TreeEntityDefinition, hooks) as unknown as BaseEntityService<T>
  }

  if (definition.type === 'dag') {
    const DagService = await getDagService()
    return new DagService<T>(ctx, definition as DagEntityDefinition, hooks) as unknown as BaseEntityService<T>
  }

  return createEntityService(ctx, definition, options)
}

/**
 * Create services for all definitions in a module
 */
export async function createModuleServices(
  ctx: ModuleContext,
  definitions: EntityDefinition[]
): Promise<Map<string, EntityService>> {
  const services = new Map<string, EntityService>()

  for (const definition of definitions) {
    const key = getServiceKey(definition)
    const service = await createEntityServiceAsync(ctx, definition) as unknown as EntityService
    services.set(key, service)
  }

  return services
}

/**
 * Get the key used to store service in ctx.services
 */
export function getServiceKey(definition: EntityDefinition): string {
  if ('table' in definition && definition.table) {
    return definition.table
  }
  if ('key' in definition && definition.key) {
    return definition.key
  }
  return resolveLocalized(definition.label, 'en').toLowerCase().replace(/\s+/g, '_')
}

